/* biome-disable lint/suspicious/noExplicitAny */

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@arcadeai/design-system/components/ui/atoms/accordion";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { OAUTH2_PROVIDER } from "@arcadeai/design-system/metadata/oauth-providers";
import { Form } from "@arcadeai/design-system/rhf";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import type { Resolver } from "react-hook-form";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useComputedRedirectUri } from "@/app/oauth/api/get-callback-url-template";
import {
  useCreateOAuthProvider,
  useUpdateOAuthProvider,
} from "@/app/oauth/api/hooks";
import { DocsAlert } from "@/app/oauth/list/components/docs-alert";
import {
  type AuthProviderCustom,
  type AuthProviderCustomForm,
  AuthProviderSchemaCustom,
  defaultValues,
  type OAuthProviderItem,
} from "@/app/oauth/types";
import { RedirectUriEditField } from "../redirect-uri-edit-field";
import { RedirectUriField } from "../redirect-uri-field";
import { AuthorizeRequestFields } from "./fields/advanced/authorize-request-fields";
import { PkceFields } from "./fields/advanced/pkce-fields";
import { RefreshTokenFields } from "./fields/advanced/refresh-token-fields";
import { TokenIntrospectionFields } from "./fields/advanced/token-introspection-fields";
import { TokenRequestFields } from "./fields/advanced/token-request-fields";
import { AuthorizationEndpointField } from "./fields/authorization-endpoint-field";
import { ClientCredentialsFields } from "./fields/client-credentials-fields";
import { DescriptionField } from "./fields/description-field";
import { IdField } from "./fields/id-field";
import { TokenEndpointField } from "./fields/token-endpoint-field";

type Props = {
  oauth?: OAuthProviderItem;
  handleClose: () => void;
  freshExternalId: string;
};

export const CustomForm = ({
  oauth,
  handleClose,
  freshExternalId: providedExternalId,
}: Props) => {
  const [openSections, setOpenSections] = useState<string[]>([]);
  const isEditing = !!oauth;
  const { redirectUri: computedRedirectUri, isLoading: configLoading } =
    useComputedRedirectUri(isEditing ? "" : providedExternalId);

  // Local edit-only toggle state
  const [useLegacyRedirectUri, setUseLegacyRedirectUri] = useState<boolean>(
    oauth?.oauth2.use_legacy_redirect_uri ?? false
  );

  const form = useForm<AuthProviderCustomForm>({
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    resolver: zodResolver(
      AuthProviderSchemaCustom
    ) as unknown as Resolver<AuthProviderCustomForm>,
    defaultValues: oauth
      ? {
          ...defaultValues,
          ...oauth,
          external_id: "", // Not used when editing, but required by schema
          binding: { type: oauth.binding?.type || "project" },
          oauth2: {
            ...oauth.oauth2,
            client_secret: "", // Keep empty for editing - only update if user enters new value
          },
        }
      : {
          ...defaultValues,
          external_id: providedExternalId,
        },
  });

  const { mutateAsync: createOAuthProvider } = useCreateOAuthProvider();
  const { mutateAsync: updateOAuthProvider } = useUpdateOAuthProvider({
    oauthId: oauth?.id ?? "",
    binding: oauth?.binding?.type || "project",
  });

  useEffect(() => {
    const errors = form.formState.errors;
    const sections: string[] = [];

    if (errors.oauth2?.pkce) {
      sections.push("pkce");
    }
    if (errors.oauth2?.authorize_request) {
      sections.push("authorization");
    }
    if (errors.oauth2?.token_request) {
      sections.push("token");
    }
    if (errors.oauth2?.refresh_request) {
      sections.push("refresh-token");
    }
    if (errors.oauth2?.token_introspection_request) {
      sections.push("token-introspection");
    }

    if (sections.length > 0) {
      setOpenSections((prev) => [...new Set([...prev, ...sections])]);
    }
  }, [form.formState.errors]);

  async function handleSubmit(values: AuthProviderCustomForm) {
    const clientSecret = values.oauth2.client_secret?.trim() ?? "";
    if (!isEditing && clientSecret === "") {
      form.setError("oauth2.client_secret", {
        message: "Client Secret is required",
      });
      toast.error("Client Secret is required");
      return;
    }

    // When introspection is disabled: on edit, send `{ endpoint: "" }` so
    // the backend disables existing introspection; on create, omit entirely.
    const introspectionDisabled =
      values.oauth2.token_introspection_request?.enabled === false;
    const {
      token_introspection_request: _existingIntrospection,
      ...oauth2WithoutIntrospection
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    } = values.oauth2 as AuthProviderCustom["oauth2"];
    let oauth2Base: AuthProviderCustom["oauth2"];
    if (!introspectionDisabled) {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      oauth2Base = values.oauth2 as AuthProviderCustom["oauth2"];
    } else if (isEditing) {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      oauth2Base = {
        ...oauth2WithoutIntrospection,
        token_introspection_request: { endpoint: "" },
      } as AuthProviderCustom["oauth2"];
    } else {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      oauth2Base = oauth2WithoutIntrospection as AuthProviderCustom["oauth2"];
    }

    try {
      if (isEditing) {
        const { client_secret: _clientSecret, ...oauth2WithoutSecret } =
          oauth2Base;
        const oauth2Updates =
          clientSecret === ""
            ? {
                ...oauth2WithoutSecret,
                use_legacy_redirect_uri: useLegacyRedirectUri,
              }
            : {
                ...oauth2WithoutSecret,
                client_secret: clientSecret,
                use_legacy_redirect_uri: useLegacyRedirectUri,
              };

        await updateOAuthProvider({
          // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
          ...(values as unknown as AuthProviderCustom),
          oauth2: oauth2Updates,
        });
      } else {
        await createOAuthProvider({
          // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
          ...(values as unknown as AuthProviderCustom),
          oauth2: oauth2Base,
        });
      }

      handleClose();
    } catch {
      toast.error("Unable to save OAuth provider - please try again");
    }
  }

  function handleError(_error: unknown) {
    toast.error("Some required information is missing or incorrect");
  }

  return (
    <Form {...form}>
      <form
        className="space-y-6"
        onSubmit={form.handleSubmit(handleSubmit, handleError)}
      >
        {/* Basic Fields */}
        <div className="space-y-4">
          <DocsAlert className="mt-4" provider={OAUTH2_PROVIDER} />
          {!!form.control && (
            <>
              <IdField />
              <DescriptionField />
              <ClientCredentialsFields oauth={oauth} />
              {!isEditing && (
                <RedirectUriField
                  isLoading={configLoading}
                  redirectUri={computedRedirectUri}
                />
              )}
              {!!(isEditing && oauth) && (
                <RedirectUriEditField
                  allowLegacy={oauth.oauth2.allow_legacy_redirect_uri}
                  defaultRedirectUri={oauth.oauth2.redirect_uri}
                  isLoading={false}
                  legacyRedirectUri={oauth.oauth2.legacy_redirect_uri}
                  onChangeUseLegacy={setUseLegacyRedirectUri}
                  useLegacy={useLegacyRedirectUri}
                />
              )}
              <AuthorizationEndpointField form={form} />
              <TokenEndpointField form={form} />
            </>
          )}
        </div>

        {/* Advanced Settings */}
        {!!form.control && (
          <Accordion
            className="w-full space-y-3"
            onValueChange={setOpenSections}
            type="multiple"
            value={openSections}
          >
            <AccordionItem
              className="rounded-lg border border-border/50 bg-background transition-all duration-200 hover:bg-muted/10 data-open:border-border data-open:bg-muted/20 data-open:shadow-sm"
              value="pkce"
            >
              <AccordionTrigger className="px-4 aria-expanded:font-semibold aria-expanded:text-foreground [&[aria-expanded=true]>svg]:text-foreground">
                PKCE Settings
              </AccordionTrigger>
              <AccordionContent className="border-border/30 border-t px-2.5 pt-0 pb-4">
                <div className="space-y-4 pt-4">
                  <PkceFields />
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              className="rounded-lg border border-border/50 bg-background transition-all duration-200 hover:bg-muted/10 data-open:border-border data-open:bg-muted/20 data-open:shadow-sm"
              value="authorization"
            >
              <AccordionTrigger className="px-4 aria-expanded:font-semibold aria-expanded:text-foreground [&[aria-expanded=true]>svg]:text-foreground">
                Authorization Settings
              </AccordionTrigger>
              <AccordionContent className="border-border/30 border-t px-2.5 pt-0 pb-4">
                <div className="space-y-4 pt-4">
                  <AuthorizeRequestFields />
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              className="rounded-lg border border-border/50 bg-background transition-all duration-200 hover:bg-muted/10 data-open:border-border data-open:bg-muted/20 data-open:shadow-sm"
              value="token"
            >
              <AccordionTrigger className="px-4 aria-expanded:font-semibold aria-expanded:text-foreground [&[aria-expanded=true]>svg]:text-foreground">
                Token Settings
              </AccordionTrigger>
              <AccordionContent className="border-border/30 border-t px-2.5 pt-0 pb-4">
                <div className="space-y-4 pt-4">
                  <TokenRequestFields />
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              className="rounded-lg border border-border/50 bg-background transition-all duration-200 hover:bg-muted/10 data-open:border-border data-open:bg-muted/20 data-open:shadow-sm"
              value="refresh-token"
            >
              <AccordionTrigger className="px-4 aria-expanded:font-semibold aria-expanded:text-foreground [&[aria-expanded=true]>svg]:text-foreground">
                Refresh Token Settings
              </AccordionTrigger>
              <AccordionContent className="border-border/30 border-t px-2.5 pt-0 pb-4">
                <div className="space-y-4 pt-4">
                  <RefreshTokenFields />
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              className="rounded-lg border border-border/50 bg-background transition-all duration-200 hover:bg-muted/10 data-open:border-border data-open:bg-muted/20 data-open:shadow-sm"
              value="token-introspection"
            >
              <AccordionTrigger className="px-4 aria-expanded:font-semibold aria-expanded:text-foreground [&[aria-expanded=true]>svg]:text-foreground">
                Token Introspection Settings
              </AccordionTrigger>
              <AccordionContent className="border-border/30 border-t px-2.5 pt-0 pb-4">
                <div className="space-y-4 pt-4">
                  <TokenIntrospectionFields />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        <Button className="mb-6 w-full" type="submit">
          {isEditing ? "Update" : "Create"}
        </Button>
      </form>
    </Form>
  );
};
